"use client";
import { getMediaUrl } from "@/lib/utils";
import Footer from "@/components/ar-IQ/Footer";
import Navbar from "@/components/ar-IQ/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";
import blogsData from "@/data/blogs.json";
import { getBlogs, getNews } from "@/lib/api";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";

const NewsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [floatingParticles, setFloatingParticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroData, setHeroData] = useState({
    title: "أحدث الأخبار\nوالمقالات",
    titleHighlight: "",
    subtitle: "اطلع على آخر الأخبار والمقالات في عالم الإعلان والتسويق",
    video: null,
  });
  const { lang } = useLanguage();

  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  // Determine hero poster/background: prefer hero video poster, then hero image, then default
  const heroPoster = (() => {
    if (heroData.video?.poster) {
      const p = heroData.video.poster;
      if (typeof p === "string") {
        return p.startsWith("http") ? p : getMediaUrl(p);
      }
      if (typeof p === "object" && p?.url) return p.url;
    }
    if (heroData.image?.url) return heroData.image.url;
    if (heroData.poster) return heroData.poster;
    return getMediaUrl("arabic.jpg");
  })();
  // Determine hero video src (prefer full URL, accept object with .url, or basename via getMediaUrl)
  const heroVideoSrc = (() => {
    const v = heroData.video;
    if (!v) return null;
    if (typeof v === "string") return v.startsWith("http") ? v : getMediaUrl(v);
    if (typeof v === "object") {
      if (v.url) return v.url;
      if (v.publicId) return getMediaUrl(v.publicId);
      if (v.name) return getMediaUrl(v.name);
    }
    return null;
  })();

  useEffect(() => {
    // Debug: log computed hero media sources
    console.log("[NewsPage-AR] heroVideoSrc:", heroVideoSrc);
    console.log("[NewsPage-AR] heroPoster:", heroPoster);
  }, [heroVideoSrc, heroPoster]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Use "ar" directly for Arabic page instead of relying on lang from context
        const [allBlogsResponse, newsData] = await Promise.all([
          getBlogs("ar", {}).catch(() => []),
          getNews("ar"),
        ]);

        // Use backend data if available, otherwise fallback to static JSON
        const allBlogs =
          Array.isArray(allBlogsResponse) && allBlogsResponse.length > 0
            ? allBlogsResponse
            : blogsData;

        setBlogs(allBlogs);
        setFilteredBlogs(allBlogs);
        console.log("News Data (Arabic page):", newsData);
        console.log("Hero title:", newsData?.hero?.title);
        console.log("Hero subtitle:", newsData?.hero?.subtitle);

        // Set hero data from API
        if (newsData?.hero) {
          // After localization, title and subtitle should be strings
          // But handle both cases: string (after localization) or object (if localization didn't run)
          const title =
            typeof newsData.hero.title === "string"
              ? newsData.hero.title || "أحدث الأخبار\nوالمقالات"
              : newsData.hero.title?.ar ||
                newsData.hero.title?.en ||
                "أحدث الأخبار\nوالمقالات";

          const subtitle =
            typeof newsData.hero.subtitle === "string"
              ? newsData.hero.subtitle ||
                "اطلع على آخر الأخبار والمقالات في عالم الإعلان والتسويق"
              : newsData.hero.subtitle?.ar ||
                newsData.hero.subtitle?.en ||
                "اطلع على آخر الأخبار والمقالات في عالم الإعلان والتسويق";

          const titleHighlight =
            typeof newsData.hero.titleHighlight === "string"
              ? newsData.hero.titleHighlight || ""
              : newsData.hero.titleHighlight?.ar ||
                newsData.hero.titleHighlight?.en ||
                "";

          setHeroData({
            title,
            titleHighlight,
            subtitle,
            video: newsData.hero.video,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setBlogs([]);
        setFilteredBlogs([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Generate floating particles
    const particles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 6 + Math.random() * 4,
    }));
    setFloatingParticles(particles);
  }, []);

  useEffect(() => {
    if (loading) return;

    const filterBlogs = () => {
      try {
        let filtered = blogs;

        // Filter by search query
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter((blog) => {
            const title =
              typeof blog.title === "string"
                ? blog.title
                : blog.title?.ar || "";
            const excerpt =
              typeof blog.excerpt === "string"
                ? blog.excerpt
                : blog.excerpt?.ar || "";
            const content =
              typeof blog.content === "string"
                ? blog.content
                : blog.content?.ar || "";

            return (
              title.toLowerCase().includes(query) ||
              excerpt.toLowerCase().includes(query) ||
              content.toLowerCase().includes(query)
            );
          });
        }

        setFilteredBlogs(filtered);
      } catch (error) {
        console.error("Error filtering blogs:", error);
        setFilteredBlogs([]);
      }
    };

    filterBlogs();
  }, [searchQuery, blogs, loading]);

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Navbar />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        {/* Background Video */}
        <div className="absolute inset-0 will-change-transform">
          {heroVideoSrc ? (
            <div>
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                poster={heroPoster}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={heroVideoSrc} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
            </div>
          ) : (
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url('${heroPoster}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
            </div>
          )}
          {!heroVideoSrc && (
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
          )}
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {floatingParticles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute w-3 h-3 bg-[#dfa428]/40 rounded-full"
              style={{
                top: `${particle.top}%`,
                left: `${particle.left}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: particle.duration,
                repeat: Number.POSITIVE_INFINITY,
                delay: particle.delay,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight whitespace-pre-line"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.8 }}
            >
              {heroData.title.split("\n").map((line, idx) => (
                <React.Fragment key={idx}>
                  {idx === 1 ? (
                    <span className="text-[#dfa428]">{line}</span>
                  ) : (
                    line
                  )}
                  {idx < heroData.title.split("\n").length - 1 && <br />}
                </React.Fragment>
              ))}
              {heroData.titleHighlight && (
                <>
                  <br />
                  <span className="text-[#dfa428]">
                    {heroData.titleHighlight}
                  </span>
                </>
              )}
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl mb-12 leading-relaxed opacity-90"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2 }}
            >
              {heroData.subtitle}
            </motion.p>
          </motion.div>
        </div>

        {/* Geometric Shapes */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 bg-[#dfa428]/20 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 12, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{ duration: 8, repeat: Number.POSITIVE_INFINITY }}
        />
      </motion.section>

      {/* Filter and Search Section */}
      <section
        ref={sectionRef}
        className="py-20 bg-gray-50 relative overflow-hidden"
      >
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Search Bar */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="ابحث في المقالات..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 text-lg rounded-full border-2 border-gray-200 focus:border-[#0a367b] focus:outline-none transition-all duration-300 shadow-lg"
                />
                <motion.div
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  whileHover={{ scale: 1.1 }}
                >
                  🔍
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">⏳</div>
              <h3 className="text-2xl font-bold text-[#0a367b] mb-4">
                جاري تحميل المقالات...
              </h3>
            </div>
          ) : (
            <>
              {/* Articles Grid */}
              <motion.div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
              >
                {Array.isArray(filteredBlogs) &&
                  filteredBlogs
                    .filter(
                      (blog) =>
                        blog &&
                        typeof blog === "object" &&
                        (blog.id || blog.slug)
                    )
                    .map((blog, index) => {
                      const safeId =
                        typeof blog.id === "string"
                          ? blog.id
                          : String(blog.id || index);
                      return (
                        <BlogCard key={safeId} blog={blog} index={index} />
                      );
                    })}
              </motion.div>

              {Array.isArray(filteredBlogs) && filteredBlogs.length === 0 && (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <div className="text-6xl mb-4">📝</div>
                  <h3 className="text-2xl font-bold text-[#0a367b] mb-4">
                    لا توجد مقالات
                  </h3>
                  <p className="text-gray-600">
                    لم يتم العثور على مقالات تطابق البحث المحدد
                  </p>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const BlogCard = ({ blog, index }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const rotate = useTransform(scrollYProgress, [0, 1], [5, -5]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  return (
    <motion.div ref={cardRef} style={{ y, rotate, scale }}>
      <motion.article
        className="bg-white rounded-3xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-500 transform-gpu"
        initial={{ opacity: 0, y: 100 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay: index * 0.1 }}
        whileHover={{
          scale: 1.02,
          y: -10,
          transition: { duration: 0.3 },
        }}
      >
        <div className="relative overflow-hidden h-64">
          {(() => {
            const computeImageSrc = (img) => {
              if (!img) return null;
              if (typeof img === "string") return img.startsWith("http") ? img : getMediaUrl(img);
              if (typeof img === "object") {
                if (img.url) return img.url;
                if (img.publicId) return getMediaUrl(img.publicId);
                if (img.name) return getMediaUrl(img.name);
              }
              return null;
            };

            const imageSrc = blog.imageBase64 || computeImageSrc(blog.image) || null;

            return imageSrc ? (
              <motion.div
                className="relative w-full h-full"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <Image
                  src={imageSrc}
                  alt={
                    typeof blog.title === "string"
                      ? blog.title
                      : blog.title?.ar || ""
                  }
                  fill
                  className="object-cover"
                />
              </motion.div>
            ) : (
              <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-4xl">📝</span>
              </div>
            );
          })()}

          {/* Floating Elements */}
          <motion.div
            className="absolute top-2 left-2 w-3 h-3 bg-[#dfa428]/60 rounded-full"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              delay: index * 0.3,
            }}
          />
        </div>

        <div className="p-6">
          <motion.div
            className="text-sm text-gray-500 mb-3 flex items-center space-x-2 space-x-reverse"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: index * 0.1 + 0.3 }}
          >
            <span>📅</span>
            <span>
              {(() => {
                if (!blog.date) return "";
                try {
                  const dateStr =
                    typeof blog.date === "string"
                      ? blog.date
                      : String(blog.date || "");
                  if (!dateStr) return "";
                  const d = new Date(dateStr);
                  return isNaN(d.getTime())
                    ? dateStr
                    : d.toLocaleDateString("ar-SA", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      });
                } catch {
                  return "";
                }
              })()}
            </span>
            {(() => {
              const readTime =
                typeof blog.readTime === "string"
                  ? blog.readTime
                  : blog.readTime?.ar || blog.readTime?.en || "";
              return readTime ? (
                <>
                  <span>•</span>
                  <span>⏱️</span>
                  <span>{readTime}</span>
                </>
              ) : null;
            })()}
          </motion.div>

          <motion.h2
            className="text-xl font-bold text-[#0a367b] mb-4 leading-tight group-hover:text-yellow-600 transition-colors duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1 + 0.4 }}
          >
            {typeof blog.title === "string"
              ? blog.title
              : blog.title?.ar || blog.title?.en || ""}
          </motion.h2>

          <motion.p
            className="text-gray-600 mb-6 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1 + 0.5 }}
          >
            {typeof blog.excerpt === "string"
              ? blog.excerpt
              : blog.excerpt?.ar || blog.excerpt?.en || ""}
          </motion.p>

          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: index * 0.1 + 0.6 }}
          >
            <Link
              href={`/ar-IQ/news/${
                typeof blog.slug === "string"
                  ? blog.slug
                  : blog.slug?.en || blog.slug?.ar || ""
              }`}
            >
              <motion.button
                className="text-[#0a367b] font-bold hover:text-yellow-600 transition-colors duration-300 flex items-center space-x-2 space-x-reverse group"
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>اقرأ المزيد</span>
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Number.POSITIVE_INFINITY,
                  }}
                >
                  ←
                </motion.span>
              </motion.button>
            </Link>

            <motion.div
              className="text-sm text-gray-400"
              whileHover={{ scale: 1.05 }}
            >
              {typeof blog.author === "string"
                ? blog.author
                : blog.author?.ar || blog.author?.en || ""}
            </motion.div>
          </motion.div>
        </div>
      </motion.article>
    </motion.div>
  );
};

export default NewsPage;
